import { Controller } from "@hotwired/stimulus"

const NS = ".yardiLeaseTerms";

// Paired with the shared `datatable` controller on the `.panel-body` inside this mount: that one
// owns the DataTables lifecycle, this one owns the per-row minimum-lease-term selects and the
// edits they accumulate. Mounted on the whole `.panel` rather than alongside `datatable` because
// the Save Changes form sits in `.panel-heading`, outside the datatable's own mount.
//
// Nothing is persisted until that form is submitted, so a pick is held here keyed by property id
// and re-applied on every draw: DataTables rebuilds every row from the ajax response on a search
// or a page change, which would otherwise throw away the unsaved <select> values.
export default class extends Controller {
  static get targets() {
    return ["table"];
  }

  connect() {
    this.pendingChanges = {};
    this.onDraw = this.onDraw.bind(this);

    // DataTables announces "draw.dt" through jQuery's own event bus rather than a native dispatch,
    // so a data-action never sees it; bind with jQuery, namespaced, and drop it in disconnect().
    $(this.tableTarget).on(`draw.dt${NS}`, this.onDraw);
  }

  disconnect() {
    $(this.tableTarget).off(NS);
  }

  onDraw() {
    this.applyPendingChanges();
  }

  // Wired via data-action on each row's select, set by
  // YardiPropertyDecorator#datatable_lease_term_selector.
  //
  // `data-initial-value` holds the term the row was rendered with, so picking the original value
  // again drops the row from the pending set rather than submitting a no-op. Both sides of the
  // comparison are strings here, which is what the Angular original spelled out with an explicit
  // String() around jQuery's `.data()` -- that coercion turned "12" into 12, `dataset` does not.
  onLeaseTermChange(event) {
    const select = event.currentTarget;
    const propertyId = select.dataset.propertyId;

    if (select.value === select.dataset.initialValue) {
      delete this.pendingChanges[propertyId];
    } else {
      this.pendingChanges[propertyId] = select.value;
    }
  }

  // Wired via data-action on #lease-terms-form. Forms are native in this app
  // (CONVENTIONS.md, Turbo), so fields appended during the submit event are still part of the
  // request the browser goes on to send.
  appendPendingChanges(event) {
    const form = event.currentTarget;

    Object.keys(this.pendingChanges).forEach((propertyId) => {
      const field = document.createElement("input");

      field.type = "hidden";
      field.name = `lease_terms[${propertyId}]`;
      field.value = this.pendingChanges[propertyId];
      form.appendChild(field);
    });
  }

  // Reads the live DOM rather than Stimulus targets: DataTables replaces the rows and fires
  // "draw.dt" synchronously, before Stimulus's MutationObserver has seen them, so a target list
  // would still hold the elements that were just discarded.
  applyPendingChanges() {
    this.tableTarget.querySelectorAll("select.lease-term-select").forEach((select) => {
      const pending = this.pendingChanges[select.dataset.propertyId];

      if (pending !== undefined) {
        select.value = pending;
      }
    });
  }
}
